import { format, isBefore, sub, toDate } from "date-fns";
import { Country } from "../API";
import { gqlQuery } from "./apiService";

export enum HourCategories {
  "standard" = "standard",
  "overtime" = "overtime",
  "sick" = "sick",
  "holiday" = "holiday",
}

export const toIntervalString = ({ start, end }: Interval, limiter = " - ") =>
  `${format(start, "dd/MM/yyyy")}${limiter}${format(end, "dd/MM/yyyy")}`;

export const toDbDates = ({ start, end }: Interval) =>
  [start, end].map((date) => toDate(date).toLocaleDateString("sv"));

export const filterNull = async <T extends object>(
  arr: (T | null)[] | undefined,
  exFilter?: (item: T) => boolean
) =>
  exFilter === undefined
    ? (arr ?? []).filter((i): i is T => i !== null)
    : (arr ?? []).filter((i): i is T => i !== null).filter(exFilter);

export const getCompanyClientIDs = (id: string) => {
  const { queryFn: getCompany, ...rest } = gqlQuery({
    req: "getCompany",
    vars: { id },
    key: [id],
  });

  return {
    ...rest,
    enabled: !!id,
    queryFn: async () => {
      const { data } = await getCompany();
      const ids = await filterNull(data?.getCompany?.clients?.items);
      return {
        name: data?.getCompany?.name,
        clientFilter: ids.length
          ? {
              or: ids.map(({ id }) => ({ clientID: { contains: id } })),
            }
          : undefined,
      };
    },
  };
};

export const checkIfIsBefore = (
  inputDate: Date,
  interval: number,
  duration: string
): boolean => {
  // Calculate the interval to subtract from today's date
  const intervalObject = {
    [duration]: interval,
  };
  const intervalAgo = sub(new Date(), intervalObject);

  // Check if the date is before the interval ago date
  return isBefore(inputDate, intervalAgo);
};

export const toAWSDate = (date?: Date | null): string | null =>
  date ? format(date, "yyyy-MM-dd") : null;

export const getBusinessDayDifference = (
  startDate: Date,
  endDate: Date
): number => {
  if (startDate > endDate) {
    throw new Error("Start date must be before or equal to the end date.");
  }

  let count = 0;
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Exclude Sundays (0) and Saturdays (6)
      count++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return count;
};

export const mapOptions = (values: { [key: string]: string }) => {
  if (values) {
    return Object.keys(values).map((key: string) => ({
      label: values[key],
      value: key,
    }));
  }
  return [];
};

export const countryOptions = [
  { label: "All Countries", value: "", disabled: true, key: "" },
  ...Object.values(Country).map((type) => ({
    label: type,
    value: type,
    key: type,
  })),
];

export const formatHoursChange = (
  value: number | string | null | undefined,
  limit = 24
) => {
  if (value === null || value === undefined) return 0;

  const numberValue = Number(value);
  if (isNaN(numberValue)) return 0;

  return numberValue > limit ? limit : numberValue < 0 ? 0 : numberValue;
};

export const parseDate = (dateString: string): Date => {
  const dt = new Date(dateString);
  const dtDateOnly = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000
  );

  return dtDateOnly;
};
